import {
  MutableRefObject,
  useLayoutEffect, useRef, useMemo,
} from 'react';

import Image from './Image';
import Video from './Video';
import Audio from './Audio';
import AdaptrFeedFm from './AdaptrFeedFm';
import WaveSurfer from './WaveSurfer';

import Resource, { ResourceSourceType } from './Resource';
import MediaPlayer from './MediaPlayer';

interface IMediaPlayerTarget {
  (target: Audio | AdaptrFeedFm | Video | WaveSurfer): void;
}

interface IMediaPlayerEvent {
  (event: {
    type: string,
    target?: Audio | AdaptrFeedFm | Video | WaveSurfer,
  }): void;
}

interface IMediaResourceConfig {
  beforeStart?: IMediaPlayerTarget;
  afterStop?: IMediaPlayerTarget;
  startOnMount?: boolean;
  stopOnUnmount?: boolean;
  handleMediaEnd?: IMediaPlayerEvent;
}

interface IMediaPlayer {
  play(before?: IMediaPlayerTarget): Audio | AdaptrFeedFm | Video | WaveSurfer | undefined;
  stop(after?: IMediaPlayerTarget): Audio | AdaptrFeedFm | Video | WaveSurfer | undefined;
  isPlaying(): boolean;
}

interface IMediaResource {
  mediaRef: MutableRefObject<Audio | AdaptrFeedFm | Video | WaveSurfer | Image | undefined>;
  mediaPlayerRef: MutableRefObject<IMediaPlayer | undefined>;
  containerRef: MutableRefObject<any>;
  status: 'loading' | 'error' | 'ready' | undefined;
}

export const useMediaResource = (resourceSource: ResourceSourceType | undefined | any, config?: IMediaResourceConfig): IMediaResource => {
  const mediaRef = useRef<Audio | AdaptrFeedFm | Video | WaveSurfer | Image>();
  const mediaPlayerRef = useRef<IMediaPlayer>();
  const containerRef = useRef(null);
  const resourceId = ((resourceSource as ResourceSourceType) || {}).id || '';
  const resource = Resource?.get(resourceId);
  const startOnMount = config?.startOnMount || true;
  const stopOnUnmount = config?.stopOnUnmount || true;
  const beforeStart = config?.beforeStart;
  const afterStop = config?.afterStop;
  const handleMediaEnd = config?.handleMediaEnd;

  useLayoutEffect(() => {
    if (typeof resource === 'undefined' || typeof resource.object === 'undefined') {
      return () => {};
    }

    const object: Audio | AdaptrFeedFm | Video | WaveSurfer | Image = resource?.object;
    mediaRef.current = object;
    mediaPlayerRef.current = {
      play: (before) => {
        if (before) {
          before(object as Audio | AdaptrFeedFm | Video | WaveSurfer);
        }
        MediaPlayer.play(resourceId);
        return object as Audio | AdaptrFeedFm | Video | WaveSurfer;
      },
      stop: (after) => {
        MediaPlayer.stop(resourceId);
        if (after) {
          after(object as Audio | AdaptrFeedFm | Video | WaveSurfer);
        }
        return object as Audio | AdaptrFeedFm | Video | WaveSurfer;
      },
      isPlaying: () => MediaPlayer.isPlaying(resourceId),
    };

    if (object instanceof Image && containerRef.current) {
      const current = (containerRef.current as any);
      const element = object.getElement();
      element.setAttribute('data-type', 'media-image');
      current?.appendChild(element);
      return () => {
        if (current) {
          current?.removeChild(element);
        }
      };
    }

    if (object instanceof Video && containerRef.current) {
      const current = (containerRef.current as any);
      const element = (object.getElement() as HTMLVideoElement);
      current?.appendChild(element);
      beforeStart?.(object);
      let prevCurrentTime = 0;
      const removeEndListener = object.addEventListener(Video.EVENT.TIME_UPDATE, (event: any) => {
        const target = event.target as Video;
        const { currentTime } = target.getElement();
        const { isLooping } = target;
        if ((prevCurrentTime === 0 && currentTime === 0) || prevCurrentTime < currentTime) {
          prevCurrentTime = currentTime;
          return;
        }
        handleMediaEnd?.({
          type: isLooping() ? 'restart' : 'end',
          target: object,
        });
        prevCurrentTime = currentTime;
      });
      if (startOnMount) {
        MediaPlayer.play(resourceId);
      }
      return () => {
        if (stopOnUnmount) {
          MediaPlayer.stop(resourceId);
        }
        if (removeEndListener) {
          removeEndListener();
        }
        afterStop?.(object);
        if (current) {
          current?.removeChild(element);
        }
      };
    }

    if (object instanceof Audio) {
      beforeStart?.(object);
      if (startOnMount) {
        MediaPlayer.play(resourceId);
      }
      return () => {
        if (stopOnUnmount) {
          MediaPlayer.stop(resourceId);
        }
        afterStop?.(object);
      };
    }

    if (object instanceof AdaptrFeedFm) {
      beforeStart?.(object);
      if (startOnMount) {
        MediaPlayer.play(resourceId);
      }
      return () => {
        if (stopOnUnmount) {
          MediaPlayer.stop(resourceId);
        }
        afterStop?.(object);
      };
    }

    if (object instanceof WaveSurfer && (containerRef.current as any) instanceof HTMLElement) {
      const current = (containerRef.current as any);
      const element = (object.getElement() as HTMLElement);
      current?.appendChild(element);
      const removeEndListener = object.addEventListener(WaveSurfer.EVENT.FINISH, (event: any) => {
        const target = event.target as WaveSurfer;
        const { isLooping } = target;
        handleMediaEnd?.({
          type: isLooping() ? 'restart' : 'end',
          target: object,
        });
      });
      return () => {
        if (removeEndListener) {
          removeEndListener();
        }
        if (current) {
          current?.removeChild(element);
        }
      };
    }

    return () => {};
  }, [
    resourceSource,
    resource,
    resourceId,

    startOnMount,
    stopOnUnmount,
    beforeStart,
    afterStop,
    handleMediaEnd,
  ]);

  const result = useMemo(() => ({
    mediaRef,
    mediaPlayerRef,
    containerRef,
    status: resource?.status,
  }), []);

  if (typeof resource === 'undefined' || typeof resource.object === 'undefined') {
    return result;
  }

  return result;
};
